import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useAppState } from "../AppState";
import { api } from "../lib/api";
import {
  AddrLink,
  ErrorMsg,
  Loading,
  OpKindPill,
  LiveBadge,
  OpLink,
  PageHeader,
  Segmented,
  SlotPill,
} from "../components/Bits";
import {
  firstIncludedSlot,
  fmtMas,
  formatRelative,
  slotTimestampMs,
} from "../lib/format";
import { useChainParams } from "../hooks/useChainParams";
import { Paginator, usePaged } from "../components/Paginator";
import type { OperationKind, StoredOperation } from "../lib/types";

const GROUP: Record<OperationKind, "tx" | "sc" | "roll" | "other"> = {
  transaction: "tx",
  call_sc: "sc",
  execute_sc: "sc",
  roll_buy: "roll",
  roll_sell: "roll",
  unknown: "other",
};

const FILTERS = [
  { key: "all", label: "All", short: "All" },
  { key: "tx", label: "Transactions", short: "Transfers" },
  { key: "sc", label: "Smart contracts", short: "Contracts" },
  { key: "roll", label: "Rolls", short: "Rolls" },
] as const;

/** The one-line gist of an op: amount, called function, rolls or bytecode size. */
function opDetail(op: StoredOperation): { text: string; code: boolean } {
  const d = op.details ?? {};
  switch (op.kind) {
    case "transaction":
      return { text: fmtMas(d.amount_nmas), code: false };
    case "roll_buy":
    case "roll_sell":
      return {
        text: d.roll_count != null ? `${d.roll_count.toLocaleString("en-US")} roll${d.roll_count === 1 ? "" : "s"}` : "—",
        code: false,
      };
    case "call_sc":
      return { text: d.target_function ? `${d.target_function}()` : "—", code: true };
    case "execute_sc":
      return {
        text: d.bytecode_size != null ? `bytecode · ${(d.bytecode_size / 1024).toFixed(1)} kB` : "—",
        code: false,
      };
    default:
      return { text: "—", code: false };
  }
}

/** Final status wins over candidate; no inclusion yet means pending. */
function opStatus(op: StoredOperation, included: boolean) {
  const s = op.final_exec_status ?? op.candidate_exec_status;
  if (!included || !s) return { label: "Pending", dot: "bg-muted" };
  return s === "ok"
    ? { label: "Success", dot: "bg-[#22c55e]" }
    : { label: "Failed", dot: "bg-bad" };
}

/**
 * Newest-first recent operations. Same backend as the Home panel but on its
 * own route — lets users deep-link and paginate freely without the rest of
 * the Home context.
 */
export function Operations() {
  const { client, network } = useAppState();
  const params = useChainParams();
  const paged = usePaged(50);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");

  const q = useQuery({
    queryKey: [
      "ops-page",
      network,
      client.endpoints(),
      paged.cursor,
      paged.limit,
    ],
    queryFn: () => api.recentOps(client, paged.limit, paged.cursor),
    refetchInterval: paged.page === 0 ? 10_000 : false,
  });
  useEffect(() => {
    paged.setLastResponse(q.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const all = q.data?.data ?? [];
  // ponytail: /v1/operations/recent has no kind filter, so this filters the loaded page only.
  const rows = all
    .filter((op) => filter === "all" || GROUP[op.kind] === filter)
    .map((op) => {
      const slot = firstIncludedSlot(op);
      const ts = slot ? slotTimestampMs(slot, params) : null;
      return { op, slot, ago: ts != null ? formatRelative(ts) : "pending", detail: opDetail(op), status: opStatus(op, !!slot) };
    });

  return (
    <>
      <Helmet>
        <title>{`Operations — ${network}`}</title>
      </Helmet>

      <PageHeader title="Operations" subtitle="Transfers, smart-contract calls and roll moves, newest first.">
        {paged.page === 0 && <LiveBadge seconds={10} />}
      </PageHeader>

      <section className="card p-0 sm:p-0 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-4 sm:py-5">
          <h2 className="text-base sm:text-lg font-semibold mr-auto">Latest operations</h2>
          <Segmented label="Filter by type" options={FILTERS} value={filter} onChange={setFilter} />
        </div>

        {q.isLoading ? (
          <div className="px-4 sm:px-6 pb-5"><Loading /></div>
        ) : q.isError ? (
          <div className="px-4 sm:px-6 pb-5"><ErrorMsg err={q.error} /></div>
        ) : rows.length === 0 ? (
          <div className="px-4 sm:px-6 pb-5 text-muted text-sm">
            {all.length === 0 ? "No operations yet." : "No operation on this page matches this filter."}
          </div>
        ) : (
          <>
            {/* Mobile: one card per operation. */}
            <ul className="md:hidden flex flex-col gap-2 px-4 pb-4">
              {rows.map(({ op, ago, detail, status }) => (
                <li key={op.id} className="flex flex-col gap-2.5 p-3.5 rounded-xl bg-bg border border-border">
                  <div className="flex items-center gap-2">
                    <OpKindPill kind={op.kind} />
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                    <span className="ml-auto text-xs text-muted">{ago}</span>
                  </div>
                  <div className={detail.code ? "font-mono text-[15px] text-op-call" : "text-[17px] font-semibold tabular-nums"}>
                    {detail.text}
                  </div>
                  <OpLink id={op.id} />
                  <div className="flex items-center gap-1.5 text-xs text-muted">
                    <AddrLink addr={op.creator} />
                    <Arrow />
                    {op.target ? <AddrLink addr={op.target} /> : <span>—</span>}
                    <span className="ml-auto whitespace-nowrap">fee {fmtMas(op.fee_nmas)}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[1000px]">
                <thead className="text-muted text-xs bg-thead">
                  <tr>
                    <th className="text-left py-2.5 pl-4 sm:pl-6 pr-3">Operation</th>
                    <th className="text-left py-2.5 px-3">Type</th>
                    <th className="text-left py-2.5 px-3">From → To</th>
                    <th className="text-left py-2.5 px-3">Details</th>
                    <th className="text-right py-2.5 px-3">Fee</th>
                    <th className="text-left py-2.5 px-3">Status</th>
                    <th className="text-left py-2.5 px-3" title="Slot where the operation was first included in a block.">
                      Slot
                    </th>
                    <th
                      className="text-right py-2.5 pr-4 sm:pr-6 pl-3 whitespace-nowrap"
                      title="Time when the slot was produced (or 'pending' if not yet in a block)."
                    >
                      Included
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ op, slot, ago, detail, status }) => (
                    <tr key={op.id} className="border-t border-border">
                      <td className="py-3 pl-4 sm:pl-6 pr-3">
                        <OpLink id={op.id} />
                      </td>
                      <td className="py-3 px-3">
                        <OpKindPill kind={op.kind} />
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <AddrLink addr={op.creator} />
                          <Arrow />
                          {op.target ? <AddrLink addr={op.target} /> : <span className="text-muted/50">—</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className={detail.code ? "font-mono text-[13px] text-op-call" : "tabular-nums"}>
                          {detail.text}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-muted whitespace-nowrap">
                        {fmtMas(op.fee_nmas)}
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-flex items-center gap-2 text-[13px] whitespace-nowrap">
                          <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        {slot ? <SlotPill slot={slot} /> : <span className="text-muted/50">—</span>}
                      </td>
                      <td className="py-3 pr-4 sm:pr-6 pl-3 text-right text-muted text-[13px] whitespace-nowrap">
                        {ago}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="px-4 sm:px-6 pb-4 border-t border-border">
          <Paginator
            page={paged.page}
            pageSize={paged.pageSize}
            hasMore={paged.hasMore}
            loading={q.isFetching}
            count={all.length}
            onPrev={paged.prev}
            onNext={paged.next}
            prevLabel="← Newer"
            nextLabel="Older →"
          />
        </div>
      </section>
    </>
  );
}

function Arrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted/50 shrink-0" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
